import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos, categories } from '@/mocks/videos';
import CategorySEO from './components/CategorySEO';

function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return views.toString();
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || '');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo(0, 0);
    }
  }, [slug]);

  const categoryInfo = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const filteredVideos = useMemo(
    () => videos.filter((v) => v.category === activeSlug),
    [activeSlug]
  );

  const handleCategoryChange = useCallback((newSlug: string) => {
    setActiveSlug(newSlug);
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {categoryInfo && (
        <CategorySEO
          categoryName={categoryInfo.name}
          categorySlug={activeSlug}
        />
      )}
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          {categoryInfo && (
            <Breadcrumb
              items={[
                { label: categoryInfo.name },
              ]}
            />
          )}

          <div className="mt-2 mb-8">
            {categoryInfo && (
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className={`${categoryInfo.icon} text-primary-600 text-2xl md:text-3xl`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">
                    {categoryInfo.name}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">
                    共 {filteredVideos.length} 个精彩视频
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-all duration-200 whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {filteredVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group cursor-pointer"
                  >
                    <article className="rounded-xl overflow-hidden bg-background-100 hover:bg-background-100 transition-all duration-300">
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm text-background-50 text-xs px-2 py-1 rounded-md whitespace-nowrap">
                          {video.duration}
                        </div>
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-background-50/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <i className="ri-play-fill text-primary-500 text-2xl"></i>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 md:p-4">
                        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug mb-2 group-hover:text-primary-600 transition-colors">
                          {video.title}
                        </h3>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <img
                              src={video.authorAvatar}
                              alt={video.author}
                              className="w-6 h-6 rounded-full object-cover"
                            />
                            <span className="text-xs text-foreground-500 whitespace-nowrap">{video.author}</span>
                          </div>
                          <span className="text-xs text-foreground-400 whitespace-nowrap">
                            <i className="ri-eye-line mr-1"></i>{formatViews(video.views)}
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-folder-open-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}